import { useEffect, useState } from "react";
import { chat_list_item } from "../typing/chat";
import { getLatestChats, GetLatestChatsData, getUpcomingEvents, GetUpcomingEventsData } from "@/api";
import { Request } from "@/components/CRUD";
import { appState } from "@/app/appState";
import { chatState } from "@/app/chatState";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { ActionHandler } from "@/components/UI/tools";
import { tag_list_item } from "../typing/tags";
import { shortenLabel } from "./chat/chatObjectEditorWindow/chatObjectEditorWindow";
import { EmptyRow, Pill, Row, Section } from "./settings/ui";
import { runsState } from "@/app/runsState";
import { chatPathForTags, RunBadge } from "@/components/navbar/runningChats";


const Tags = ({ names }: { names: string[] }) => (
    <div className="tag-list">
        {names.map((name) => (
            <span key={name} title={name} className="tag">{shortenLabel(name)}</span>
        ))}
    </div>
);

const ChatRow = observer(({ chat }: { chat: chat_list_item }) => {
    const navigate = useNavigate();
    const names = chat.tag_ids
        .map((tagId) => appState.tags.find((t) => t.id === tagId))
        .filter((t): t is tag_list_item => t !== undefined)
        .map((t) => t.name);
    return (
        <Row
            onClick={() => navigate(chatPathForTags(chat.tag_ids))}
            trailing={
                <div className="flex flex-row items-center gap-1 shrink-0">
                    <RunBadge run={runsState.runs.get(chat.id)} />
                    <small className="detail-text tabular-nums">{new Date(chat.updated_at).toLocaleDateString()}</small>
                </div>
            }
            chevron
            roomy
        >
            <div className="flex flex-col gap-[.35rem]">
                <Tags names={names} />
                {chat.status_message_content && (
                    <p className="summary-text line-clamp-2">{chat.status_message_content}</p>
                )}
            </div>
        </Row>
    );
});

const EventRow = observer(({ event }: { event: any }) => {
    const navigate = useNavigate();
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    const sameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();
    const durationMins = Math.round((end.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(durationMins / 60);
    const minutes = durationMins % 60;
    const durationStr =
        durationMins < 60
            ? appState.t("dashboard.durationMinutes", { n: durationMins })
            : minutes > 0
                ? appState.t("dashboard.durationHoursMinutes", { h: hours, m: minutes })
                : appState.t("dashboard.durationHours", { h: hours });
    const when = sameDay
        ? `${start.toLocaleDateString()} ${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${durationStr}`
        : `${start.toLocaleString()} – ${end.toLocaleString()}`;

    return (
        <Row
            roomy
            trailing={
                <div className="flex flex-row shrink-0">
                    <ActionHandler className="btn-ghost btn-sm" funq={() => navigate(`/workspace/chat-${event.tag_id}`)}>
                        {appState.t("dashboard.goToChat")}
                    </ActionHandler>
                    <ActionHandler className="btn-soft btn-sm" funq={() => {
                        chatState.setDelayedSelectedObjectKey({ type: "event", id: event.id });
                        navigate(`/workspace/chat-${event.tag_id}`);
                    }}>
                        {appState.t("dashboard.startTranscription")}
                    </ActionHandler>
                </div>
            }
        >
            <div className="flex flex-col gap-[.35rem]">
                <p className="l truncate">{event.title}</p>
                <div className="flex flex-row flex-wrap items-baseline gap-1">
                    {event.tag_name && <Tags names={[event.tag_name]} />}
                    <small className="detail-text">{when}</small>
                </div>
            </div>
        </Row>
    );
});

export const WorkspaceDashboard = observer(() => {

    const [latestChats, setLatestChats] = useState<chat_list_item[]>([]);
    const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const _getLatestChats = Request<GetLatestChatsData, any>(
        getLatestChats,
        () => ({}),
        setLoading,
        (data: any) => {
            setLatestChats(data.chats);
        },
        (error) => {
            setLatestChats([]);
            appState.setModal({ title: appState.t("common.error"), body: appState.t("dashboard.errorLatestChats") });
        },
        () => true,
    )

    const _getUpcomingEvents = Request<GetUpcomingEventsData, any>(
        getUpcomingEvents,
        () => ({}),
        setLoading,
        (data: any) => {
            setUpcomingEvents(data.events);
        },
        (error) => {
            setUpcomingEvents([]);
            appState.setModal({ title: appState.t("common.error"), body: appState.t("dashboard.errorUpcomingEvents") });
        },
        () => true,
    )

    useEffect(() => {
        _getLatestChats();
        _getUpcomingEvents();
    }, [])

    const taggedChats = latestChats?.filter((chat) => chat.tag_ids.length > 0) ?? [];

    return (
        <div className="min-h-screen h-full flex flex-col items-center justify-center w-full p-1 pr-2">
            <div className="flex flex-col md:flex-row md:items-start gap-2 w-full max-w-6xl">
                <div className="flex-1 min-w-0">
                    <Section
                        title={appState.t("dashboard.upcomingEvents")}
                        action={upcomingEvents?.length > 0 ? <Pill>{upcomingEvents.length}</Pill> : undefined}
                    >
                        {upcomingEvents?.length > 0
                            ? upcomingEvents.map((event) => <EventRow key={event.id} event={event} />)
                            : <EmptyRow>{appState.t("dashboard.firstRunEvents")}</EmptyRow>}
                    </Section>
                </div>

                <div className="flex-1 min-w-0">
                    <Section
                        title={appState.t("dashboard.latestChats")}
                        action={taggedChats.length > 0 ? <Pill>{taggedChats.length}</Pill> : undefined}
                    >
                        {taggedChats.length > 0
                            ? taggedChats.map((chat) => <ChatRow key={chat.id} chat={chat} />)
                            : <EmptyRow>{appState.t("dashboard.firstRunChats")}</EmptyRow>}
                    </Section>
                </div>
            </div>
        </div>
    )
});
